import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown, RefreshCcw, X } from "lucide-react";

import { useGlobalContext } from "../../../GlobalContext";
import LoadingScreen from "../../Utils/LoadingScreen";
import useVolunteerAttendance from "../../../Hooks/useVolunteerAttendance";
import { exportAnyJsonToExcel } from "../../Utils/exportAnyJsonToExcel";

import "./Attendance.css";
import useVolunteers from "../../../Hooks/Volunteer Api/useVolunteers";

const normalizeStatus = (s) => {
  if (!s) return "pending";

  const v = String(s).toLowerCase().trim();

  if (v === "f") return "full_day";
  if (v === "h") return "half_day";
  if (v === "a") return "absent";
  if (v === "p") return "pending";

  if (v.includes("full")) return "full_day";
  if (v.includes("half")) return "half_day";
  if (v.includes("absent")) return "absent";
  if (v.includes("pending")) return "pending";

  return "pending";
};

const STATUS_ORDER = ["pending", "full_day", "half_day", "absent"];

const STATUS_LABEL = {
  full_day: "F",
  half_day: "H",
  absent: "A",
  pending: "PN",
};

const STATUS_CLASS = {
  full_day: "bg-emerald-500 text-white",
  half_day: "bg-amber-400 text-slate-900",
  absent: "bg-rose-500 text-white",
  pending: "bg-blue-500 text-white",
};

const TYPE_BADGE = {
  full_time: "bg-indigo-500/10 text-indigo-700 border-indigo-200",
  part_time: "bg-sky-500/10 text-sky-700 border-sky-200",
  normal: "bg-slate-500/10 text-slate-700 border-slate-200",
};

const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

const getMonthDays = (year, month) => {
  const days = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: days }, (_, i) =>
    toYMD(new Date(year, month, i + 1)),
  );
};

const formatDay = (ymd) => new Date(ymd).getDate();

const formatWeekDay = (ymd) =>
  new Date(ymd).toLocaleDateString("en-US", { weekday: "short" });

const formatTime = (value) => {
  if (!value) return "-";
  const parts = String(value).split("T");
  return parts[1] || value;
};

const convertStatusToApi = (status) => {
  if (status === "full_day") return "F";
  if (status === "half_day") return "H";
  if (status === "absent") return "A";
  return "P";
};

const getVolunteerCode = (v) =>
  v?.volunteerCode || v?.volunteer_code || v?.driverCode || v?.code || "";

const getVolunteerName = (v) =>
  v?.name || v?.fullName || v?.username || v?.volunteer_name || "-";

const normalizeVolunteerRows = (rows, dates) => {
  return (rows || []).map((item) => {
    const volunteerCode = getVolunteerCode(item);
    const attendanceMap = {};

    if (item.attendanceMap) {
      dates.forEach((date) => {
        const existing = item.attendanceMap?.[date];

        if (typeof existing === "object" && existing !== null) {
          attendanceMap[date] = {
            status: normalizeStatus(existing.status),
            totalDutyHours: existing.totalDutyHours || "0h 0m",
            timings: Array.isArray(existing.timings) ? existing.timings : [],
          };
        } else {
          attendanceMap[date] = {
            status: normalizeStatus(existing),
            totalDutyHours: "0h 0m",
            timings: [],
          };
        }
      });
    } else {
      (item.attendance || []).forEach((a) => {
        if (!a?.date) return;

        attendanceMap[a.date] = {
          status: normalizeStatus(a.status),
          totalDutyHours: a.totalDutyHours || "0h 0m",
          timings: Array.isArray(a.timings) ? a.timings : [],
        };
      });

      dates.forEach((date) => {
        if (!attendanceMap[date]) {
          attendanceMap[date] = {
            status: "pending",
            totalDutyHours: "0h 0m",
            timings: [],
          };
        }
      });
    }

    return {
      ...item,
      volunteerCode,
      name: getVolunteerName(item),
      type: item.type || "normal",
      attendanceMap,
    };
  });
};

export default function VolunteerAttendance() {
 const { AdminId } = useGlobalContext();

  const resolvedAdminId = useMemo(() => {
    if (AdminId) return AdminId;

    try {
      const adminSaved = sessionStorage.getItem("admin");
      const volunteerSaved = sessionStorage.getItem("volunteerData");

      if (adminSaved) {
        const adminData = JSON.parse(adminSaved);
        return adminData.userId || adminData.userid || adminData.id || "";
      }

      if (volunteerSaved) {
        const volunteerData = JSON.parse(volunteerSaved);
        return (
          volunteerData.userId || volunteerData.userid || volunteerData.id || ""
        );
      }

      return "";
    } catch (error) {
      console.error("AdminId fallback parse error:", error);
      return "";
    }
  }, [AdminId]);

  const isAdminReady = Boolean(resolvedAdminId);

 const {
  attendanceData,
  setAttendanceData,
  loading,
  fetchVolunteerAttendance,
  updateVolunteerAttendance,
} = useVolunteerAttendance();

  const {
  fetchAllVolunteers,
} = useVolunteers();

  const today = new Date();
  const todayStr = toYMD(today);

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const dates = useMemo(() => getMonthDays(year, month), [year, month]);

  const [volunteerOptions, setVolunteerOptions] = useState([]);
  const [volunteerListLoading, setVolunteerListLoading] = useState(false);
  const [uiVolunteerCode, setUiVolunteerCode] = useState("");
  const [uiSearch, setUiSearch] = useState("");
  const [uiType, setUiType] = useState("all");
  const [uiStatus, setUiStatus] = useState("all");

  const [applied, setApplied] = useState({
    volunteerCode: "",
    search: "",
    type: "all",
    status: "all",
  });

  const [dirtyMap, setDirtyMap] = useState({});
  const [initialFetched, setInitialFetched] = useState(false);

  const [exportModal, setExportModal] = useState(false);
  const [exportFromDate, setExportFromDate] = useState(todayStr);
  const [exportToDate, setExportToDate] = useState(todayStr);

  const canCallApi = isAdminReady;

  const normalizedVolunteers = useMemo(
    () => normalizeVolunteerRows(attendanceData || [], dates),
    [attendanceData, dates],
  );

  const fetchAttendanceForMonth = async (volunteerCode = "") => {
    if (!isAdminReady) return;
    if (!dates.length) return;

    setInitialFetched(false);

    await fetchVolunteerAttendance({
      volunteerCode: volunteerCode || "",
      fromDate: dates[0],
      toDate: dates[dates.length - 1],
    });

    setInitialFetched(true);
  };

  const fetchVolunteerDropdownList = async () => {
  if (!isAdminReady) return;

  setVolunteerListLoading(true);

  try {
    const list = await fetchAllVolunteers(resolvedAdminId, true);

    const normalizedList = (list || [])
      .map((item) => {
        const code = getVolunteerCode(item);
        const name = getVolunteerName(item);

        return {
          ...item,
          volunteerCode: code,
          name,
        };
      })
      .filter((item) => item.volunteerCode);

    setVolunteerOptions(normalizedList);
  } catch (error) {
    console.error("fetchVolunteerDropdownList error:", error);
    setVolunteerOptions([]);
  } finally {
    setVolunteerListLoading(false);
  }
};

  useEffect(() => {
    if (!isAdminReady) return;
    if (!dates.length) return;

    fetchAttendanceForMonth(applied.volunteerCode);
  }, [isAdminReady, year, month]);

  useEffect(() => {
  if (!isAdminReady) return;

  fetchVolunteerDropdownList();
}, [isAdminReady]);

  const onApplyFilters = () => {
    const next = {
      volunteerCode: uiVolunteerCode,
      search: uiSearch.trim(),
      type: uiType,
      status: uiStatus,
    };

    setApplied(next);
    fetchAttendanceForMonth(next.volunteerCode);
  };

  const onResetFilters = () => {
    const next = {
      volunteerCode: "",
      search: "",
      type: "all",
      status: "all",
    };

    setUiVolunteerCode("");
    setUiSearch("");
    setUiType("all");
    setUiStatus("all");

    setApplied(next);
    fetchAttendanceForMonth("");
  };

  const getCell = (volunteerCode, date, baseCell) => {
    const dirty = dirtyMap[`${volunteerCode}|${date}`];

    if (!dirty) return baseCell;

    return {
      ...baseCell,
      status: dirty.status,
    };
  };

  const cycleStatus = (volunteerCode, date, currentStatus) => {
    const safeCurrent = normalizeStatus(currentStatus);
    const next =
      STATUS_ORDER[
        (STATUS_ORDER.indexOf(safeCurrent) + 1) % STATUS_ORDER.length
      ];

    setDirtyMap((prev) => ({
      ...prev,
      [`${volunteerCode}|${date}`]: {
        volunteerCode,
        date,
        status: next,
      },
    }));
  };

const saveAll = async () => {
  const data = Object.values(dirtyMap).map((item) => ({
    volunteerCode: item.volunteerCode,
    date: item.date,
    status: convertStatusToApi(item.status),
  }));

  if (!data.length) return toast.message("No changes to save");

  const success = await updateVolunteerAttendance(data);

  if (success) {
    setAttendanceData((prev) =>
      (prev || []).map((volunteer) => {
        const volunteerCode = getVolunteerCode(volunteer);

        const updatedAttendanceMap = {
          ...(volunteer.attendanceMap || {}),
        };

        const updatedAttendance = Array.isArray(volunteer.attendance)
          ? [...volunteer.attendance]
          : [];

        data.forEach((item) => {
          if (String(item.volunteerCode) !== String(volunteerCode)) return;

          const uiStatus = normalizeStatus(item.status);

          updatedAttendanceMap[item.date] = {
            ...(updatedAttendanceMap[item.date] || {
              totalDutyHours: "0h 0m",
              timings: [],
            }),
            status: uiStatus,
          };

          const existingIndex = updatedAttendance.findIndex(
            (att) => att?.date === item.date
          );

          if (existingIndex >= 0) {
            updatedAttendance[existingIndex] = {
              ...updatedAttendance[existingIndex],
              status: item.status,
            };
          } else {
            updatedAttendance.push({
              date: item.date,
              status: item.status,
              totalDutyHours: "0h 0m",
              timings: [],
            });
          }
        });

        return {
          ...volunteer,
          volunteerCode,
          attendanceMap: updatedAttendanceMap,
          attendance: updatedAttendance,
        };
      })
    );

    setDirtyMap({});
    toast.success("Attendance updated");
  }
};

  const discardAll = () => {
    if (!Object.keys(dirtyMap).length) return;
    setDirtyMap({});
    toast.message("Changes discarded");
  };

  const filteredVolunteers = useMemo(() => {
    let list = normalizedVolunteers;

    if (applied.type !== "all") {
      list = list.filter(
        (v) => String(v.type || "").toLowerCase() === applied.type,
      );
    }

    if (applied.search) {
      const q = applied.search.toLowerCase();

      list = list.filter((v) => {
        const name = String(v.name || "")
          .toLowerCase()
          .trim();
        const code = String(v.volunteerCode || "").toLowerCase();
        return name.includes(q) || code.includes(q);
      });
    }

    if (applied.status !== "all") {
      list = list.filter((v) =>
        Object.entries(v.attendanceMap || {}).some(([date, cell]) => {
          const resolved = getCell(v.volunteerCode, date, cell);
          return normalizeStatus(resolved.status) === applied.status;
        }),
      );
    }

    return list;
  }, [normalizedVolunteers, applied, dirtyMap]);

  const handleExportDownload = () => {
    if (!filteredVolunteers.length) {
      toast.message("No data to export");
      return;
    }

    if (exportFromDate > exportToDate) {
      toast.error("From date cannot be after To date");
      return;
    }

    const exportData = filteredVolunteers.map((v) => {
      const row = {
        VolunteerCode: v.volunteerCode,
        Name: v.name || "",
        Type: v.type || "",
      };

      dates
        .filter((date) => date >= exportFromDate && date <= exportToDate)
        .forEach((date) => {
          const baseCell = v.attendanceMap?.[date] || {
            status: "pending",
            totalDutyHours: "0h 0m",
            timings: [],
          };

          const cell = getCell(v.volunteerCode, date, baseCell);
          row[`${formatDay(date)}-${formatWeekDay(date)}`] =
            STATUS_LABEL[normalizeStatus(cell.status)];
          row[`${formatDay(date)}-${formatWeekDay(date)} Hours`] =
            cell.totalDutyHours || "0h 0m";
        });

      return row;
    });

    exportAnyJsonToExcel({
      data: exportData,
      filePrefix: `Volunteer_Attendance_${exportFromDate}_to_${exportToDate}`,
      sheetName: "Attendance",
    });

    setExportModal(false);
  };

  const dirtyCount = Object.keys(dirtyMap).length;

  return (
    <div className="w-full overflow-x-hidden px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">
            Volunteer Attendance
          </h1>

          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600">
            <div className="flex items-center gap-1">
              <span className="att-legend bg-emerald-500" /> F – Full
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-amber-400" /> H – Half
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-rose-500" /> A – Absent
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-blue-500" /> PN – Pending
            </div>
            <span className="text-slate-400">
              • Hover badge for timings • Click badge to change
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => fetchAttendanceForMonth(applied.volunteerCode)}
            className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-xl shadow disabled:opacity-60"
            disabled={loading || !canCallApi}
          >
            <RefreshCcw size={16} />
            <span>Refresh</span>
          </button>

          <button
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl shadow"
            onClick={() => {
              setExportFromDate(dates[0] || todayStr);
              setExportToDate(dates[dates.length - 1] || todayStr);
              setExportModal(true);
            }}
          >
            <FileDown size={14} />
            <span>Export</span>
          </button>

          <button
            onClick={discardAll}
            disabled={loading || dirtyCount === 0}
            className="px-3 py-2 rounded-xl border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-60"
          >
            Discard ({dirtyCount})
          </button>

          <button
            onClick={saveAll}
            disabled={loading || dirtyCount === 0}
            className="px-3 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
          >
            Save Changes ({dirtyCount})
          </button>
        </div>
      </div>

      <div className="mt-4 bg-white rounded-2xl border border-slate-200">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-xs text-slate-500">Month</label>
              <input
                type="month"
                value={`${year}-${String(month + 1).padStart(2, "0")}`}
                onChange={(e) => {
                  const [y, m] = e.target.value.split("-");
                  setYear(Number(y));
                  setMonth(Number(m) - 1);
                }}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
              />
            </div>

            <div className="md:col-span-4">
              <label className="text-xs text-slate-500">Volunteer</label>
              <select
                value={uiVolunteerCode}
                onChange={(e) => setUiVolunteerCode(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
                disabled={volunteerListLoading}
              >
                <option value="">All Volunteers</option>
                {volunteerOptions.map((v) => {
                  const code = getVolunteerCode(v);
                  const name = getVolunteerName(v);

                  return (
                    <option key={code} value={code}>
                      {name} ({code})
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="text-xs text-slate-500">Search</label>
              <input
                value={uiSearch}
                onChange={(e) => setUiSearch(e.target.value)}
                placeholder="Search name or code..."
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
              />
            </div>

            <div className="md:col-span-1">
              <label className="text-xs text-slate-500">Type</label>
              <select
                value={uiType}
                onChange={(e) => setUiType(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
              >
                <option value="all">All</option>
                <option value="full_time">Full-time</option>
                <option value="part_time">Part-time</option>
                <option value="normal">Normal</option>
              </select>
            </div>

            <div className="md:col-span-1">
              <label className="text-xs text-slate-500">Status</label>
              <select
                value={uiStatus}
                onChange={(e) => setUiStatus(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
              >
                <option value="all">Any</option>
                <option value="full_day">Full</option>
                <option value="half_day">Half</option>
                <option value="absent">Absent</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div className="md:col-span-12 flex flex-wrap items-center justify-between gap-2 mt-2">
              <div className="flex gap-2">
                <button
                  onClick={onApplyFilters}
                  disabled={loading || !canCallApi}
                  className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
                >
                  Apply Filters
                </button>

                <button
                  onClick={onResetFilters}
                  disabled={loading || !canCallApi}
                  className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60"
                >
                  Reset
                </button>
              </div>

              <div className="text-xs text-slate-500">
                Showing{" "}
                <span className="font-semibold text-slate-700">
                  {filteredVolunteers.length}
                </span>{" "}
                volunteers •{" "}
                <span className="font-semibold text-slate-700">
                  {dates.length}
                </span>{" "}
                days
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading || !isAdminReady || !initialFetched ? (
        <div className="mt-4">
          <LoadingScreen />
        </div>
      ) : null}

      <div className="bg-white rounded-2xl border border-slate-200 mt-4">
        <div className="attendance-grid-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th className="attendance-th sticky-top sticky-driver text-left">
                  Vol
                </th>

                {dates.map((date) => (
                  <th
                    key={date}
                    className="bg-orange-400 attendance-th sticky-top text-center date-col"
                  >
                    <div className="flex flex-col items-center leading-tight">
                      <span className="font-semibold">{formatDay(date)}</span>
                      <span className="text-[10px] opacity-90">
                        {formatWeekDay(date)}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading && initialFetched && filteredVolunteers.length === 0 ? (
                <tr>
                  <td
                    colSpan={1 + dates.length}
                    className="py-10 text-center text-slate-500"
                  >
                    No volunteers found for this filter/month.
                  </td>
                </tr>
              ) : null}

              {filteredVolunteers.map((v) => (
                <tr key={v.volunteerCode} className="hover:bg-slate-50/70">
                  <td className="attendance-td sticky-driver">
                    <div className="min-w-0">
                      <div className="text-[12px] font-semibold text-slate-900 truncate max-w-[78px]">
                        {v.name || "-"}
                      </div>

                      <div className="text-[10px] text-slate-500">
                        {v.volunteerCode}
                      </div>

                      <span
                        className={[
                          "inline-flex items-center mt-1 px-1.5 py-0.5 rounded-md border text-[9px] font-semibold max-w-[78px] truncate",
                          TYPE_BADGE[String(v.type || "").toLowerCase()] ||
                            TYPE_BADGE.normal,
                        ].join(" ")}
                      >
                        {String(v.type || "normal").replaceAll("_", " ")}
                      </span>
                    </div>
                  </td>

                  {dates.map((date) => {
                    const baseCell = v.attendanceMap?.[date] || {
                      status: "pending",
                      totalDutyHours: "0h 0m",
                      timings: [],
                    };

                    const cell = getCell(v.volunteerCode, date, baseCell);
                    const status = normalizeStatus(cell.status);
                    const key = `${v.volunteerCode}|${date}`;
                    const isDirty = !!dirtyMap[key];

                    return (
                      <td key={date} className="attendance-td text-center">
                        <div className="attendance-cell-wrap">
                          <button
                            type="button"
                            onClick={() =>
                              cycleStatus(v.volunteerCode, date, status)
                            }
                            className={[
                              "att-badge",
                              STATUS_CLASS[status],
                              isDirty ? "ring-2 ring-slate-900/20" : "",
                            ].join(" ")}
                            title="Click to change"
                          >
                            {STATUS_LABEL[status]}
                          </button>

                          <div className="attendance-hover-card">
                            <div className="attendance-hover-head">
                              <span className="text-xs font-semibold text-slate-900">
                                {date}
                              </span>
                              <span className="text-[11px] font-semibold text-slate-600">
                                {cell.totalDutyHours || "0h 0m"}
                              </span>
                            </div>

                            <div className="attendance-hover-meta">
                              <div className="attendance-hover-line">
                                Status:{" "}
                                <b className="capitalize">
                                  {status.replaceAll("_", " ")}
                                </b>
                              </div>
                            </div>

                            <div className="attendance-hover-timings">
                              {cell.timings?.length ? (
                                cell.timings.map((t, i) => (
                                  <div
                                    key={i}
                                    className="attendance-timing-item"
                                  >
                                    <div className="attendance-timing-title">
                                      Shift {i + 1}
                                    </div>

                                    <div className="text-[11px] text-slate-600">
                                      ON: {formatTime(t.on_duty_time)}
                                    </div>
                                    <div className="text-[11px] text-slate-600">
                                      OFF: {formatTime(t.off_duty_time)}
                                    </div>
                                    <div className="text-[11px] font-semibold text-slate-700">
                                      Duration: {t.duration || "-"}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="attendance-empty-note">
                                  No duty recorded
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-slate-500">
            Volunteer column is fixed. Scroll inside grid to see all dates.
          </div>

          <div className="text-xs">
            {dirtyCount ? (
              <span className="text-rose-600 font-semibold">
                {dirtyCount} unsaved change(s)
              </span>
            ) : (
              <span className="text-emerald-600 font-semibold">
                All changes saved
              </span>
            )}
          </div>
        </div>
      </div>

      {exportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-96 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">
                Export Attendance
              </h3>

              <button
                onClick={() => setExportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-600">
                From Date
              </label>
              <input
                type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-600">
                To Date
              </label>
              <input
                type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setExportModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition"
              >
                Cancel
              </button>

              <button
                onClick={handleExportDownload}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition"
              >
                <FileDown size={15} />
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
